import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { needsReauthentication } from '../../lib/errors'
import { useI18n } from '../lib/i18n'
import { forgetTouch, passkeyErrorMessage, touchedRecently } from '../lib/passkeys'
import { useTouchPasskey } from '../lib/use-passkeys'

/*
 * The passkey again before a sensitive action (ADR 0008 §4: suspending or reactivating a company,
 * activating a course; also adding a passkey): a touch of less than 5 minutes, which the API and
 * the database check. A dialog asks for it, explains a failure, and lets the person cancel; the
 * action goes on once it has closed and the focus is back on what opened it.
 */

export interface ReauthOptions {
  /**
   * Where the focus goes back once the dialog closes: what asked for the action. By default, what
   * had the focus when it was asked for (none, if a dialog that asked has just closed).
   */
  from?: HTMLElement | null
  /** A refusal that asks for the passkey again; by default, `reauthenticate`. */
  stale?: (err: unknown) => boolean
  /**
   * What the passkey confirms, already translated (the action, its company and what it acts on),
   * shown in the dialog when nothing before it named them.
   */
  purpose?: string
}

export interface Reauth {
  /**
   * Resolves true once the passkey has been touched in the last minutes, asking for it now if
   * not; false if the person cancels.
   */
  confirm: (options?: ReauthOptions) => Promise<boolean>
  /**
   * Runs a sensitive action after `confirm`, and once more if the server still asks for the
   * passkey (`reauthenticate`, or what `stale` says). Undefined if the person cancels.
   */
  run: <T>(action: () => Promise<T>, options?: ReauthOptions) => Promise<T | undefined>
}

const ReauthContext = createContext<Reauth | null>(null)

export function ReauthProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const touch = useTouchPasskey()
  const { reset } = touch
  const [open, setOpen] = useState(false)
  const pending = useRef<((ok: boolean) => void) | null>(null)
  const outcome = useRef(false)
  const opener = useRef<HTMLElement | null>(null)
  const [purpose, setPurpose] = useState<string | null>(null)

  const close = useCallback(
    (ok: boolean) => {
      outcome.current = ok
      setOpen(false)
      reset()
    },
    [reset],
  )

  const confirm = useCallback(({ from, purpose: what }: ReauthOptions = {}) => {
    if (touchedRecently()) return Promise.resolve(true)
    opener.current =
      from ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
    setPurpose(what ?? null)
    return new Promise<boolean>((resolve) => {
      pending.current?.(false)
      pending.current = resolve
      outcome.current = false
      setOpen(true)
    })
  }, [])

  const run = useCallback(
    async <T,>(action: () => Promise<T>, options: ReauthOptions = {}) => {
      const stale = options.stale ?? needsReauthentication
      if (!(await confirm(options))) return undefined
      try {
        return await action()
      } catch (err) {
        if (!stale(err)) throw err
        forgetTouch()
        if (!(await confirm(options))) return undefined
        return action()
      }
    },
    [confirm],
  )

  const value = useMemo(() => ({ confirm, run }), [confirm, run])
  return (
    <ReauthContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close(false)
        }}
        onClosed={() => {
          pending.current?.(outcome.current)
          pending.current = null
        }}
        title={t('reauth.title')}
        confirmLabel={t('reauth.confirm')}
        busyLabel={t('reauth.busy')}
        cancelLabel={t('common.cancel')}
        busy={touch.isPending}
        error={touch.isError ? t(passkeyErrorMessage(touch.error)) : null}
        onConfirm={() => touch.mutate(undefined, { onSuccess: () => close(true) })}
        opener={opener}
      >
        {purpose ? <p className="font-medium">{purpose}</p> : null}
        <p>{t('reauth.body')}</p>
      </ConfirmDialog>
    </ReauthContext.Provider>
  )
}

export function useReauth(): Reauth {
  const value = useContext(ReauthContext)
  if (!value) throw new Error('useReauth must be used inside ReauthProvider')
  return value
}
